import * as React from 'react';
import actions, { ActionRequest } from '../actions';
import { ContainerContext, mapProps } from './';
import {
    BaseContainer,
    BaseElement,
    WarningDialog,
    LiveEventsFilter,
    Table,
    HeaderButton,
} from '../components';
import { connect } from 'react-redux';
import {
    formatDate,
    isActive,
    isActiveTimerEntity,
    isComing,
    isEnded,
    Link,
    RecurringEntity,
    truncateString,
} from '../utils';
import { Chip, Grid2 as Grid } from '@mui/material';
import {
    AddOutlined,
    BarChartOutlined,
    CalendarTodayOutlined as CalendarTodayIcon,
    DeleteOutlineOutlined,
    DeveloperModeOutlined,
    EventOutlined, LibraryAddOutlined, ModeEditOutlineOutlined,
} from '@mui/icons-material';
import {
    EventType,
    LiveEventTypeFilter,
    LiveEventStatusFilter,
    UserTitleRole,
    TimerType,
} from '../enums';
import { useNavigate } from 'react-router';
import { StateMap } from '../reducers';
import useMediaQuery from '@mui/material/useMediaQuery';

interface Props extends ContainerContext.Props {
}

type DialogType = 'DeleteLiveEvent';
interface State {
    readonly typeFilter: LiveEventTypeFilter;
    readonly statusFilter: LiveEventStatusFilter;
    readonly developerOnly: boolean;
    readonly openDialog: DialogType | null;
    readonly selectedLiveEvent: Entities.LiveEvent | null;
}

class Container extends BaseElement<Props, State> {
    state: State = {
        typeFilter: LiveEventTypeFilter.All,
        statusFilter: LiveEventStatusFilter.All,
        developerOnly: false,
        openDialog: null,
        selectedLiveEvent: null,
    };

    protected renderContainer(): React.JSX.Element {
        const buttons: HeaderButton[] | undefined = this.props.userTitleRole === UserTitleRole.Viewer ? undefined : [
            { text: 'Create', icon: AddOutlined, onClick: (event: any) => this.toLink(`/liveEvent/create`)},
        ];
        return (
            <BaseContainer
                {...this.props}
                styled={this.props.styled}
                title = "Live Events"
                TitleIcon = {CalendarTodayIcon}
                buttons = {buttons}
            >
                {this.renderContent()}
            </BaseContainer>
        );
    }

    protected renderDialogs(): React.JSX.Element {
        return (
            <>
                {this.renderDeleteLiveEventDialog()}
            </>
        );
    }

    private renderContent = () => {
        const { userTitleRole } = this.props;
        const { developerOnly, typeFilter, statusFilter } = this.state;

        const tableRef = React.createRef();

        const columns = [
            { title: 'Details', field: 'details'},
            { title: 'Schedule', field: 'schedule'},
        ];

        const onSearch = () => {
            // @ts-ignore
            tableRef.current.retry();
        };

        const onResetDeveloper = () => {
            this.setState({ developerOnly: false });
            onSearch();
        };

        const onResetTypeFilter = () => {
            this.setState({ typeFilter: LiveEventTypeFilter.All });
            onSearch();
        };

        const onResetStatusFilter = () => {
            this.setState({ statusFilter: LiveEventStatusFilter.All });
            onSearch();
        };

        const onFilter = (type: LiveEventTypeFilter, status: LiveEventStatusFilter, developer: boolean) => {
            this.setState({ typeFilter: type, statusFilter: status, developerOnly: developer });
            onSearch();
        };

        const showChips = typeFilter !== LiveEventTypeFilter.All || statusFilter !== LiveEventStatusFilter.All || developerOnly;
        const title = (
            <Grid container={true} spacing={1}>
                <Grid size={12}>
                    <LiveEventsFilter
                        styled={this.props.styled}
                        typeFilter={typeFilter}
                        statusFilter={statusFilter}
                        developerOnly={developerOnly}
                        onFilter={onFilter}
                    />
                </Grid>
                {showChips &&
                    <Grid size={12}>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gridColumnGap: 5, gridGap: 5}} >
                            {typeFilter === LiveEventTypeFilter.Regular && (
                                <Chip
                                    size={'small'}
                                    icon={<EventOutlined />}
                                    label='Event Type: "Regular"'
                                    onDelete={onResetTypeFilter}
                                />
                            )}
                            {typeFilter === LiveEventTypeFilter.LeaderBoard && (
                                <Chip
                                    size={'small'}
                                    icon={<BarChartOutlined />}
                                    label='Event Type: "Leaderboard"'
                                    onDelete={onResetTypeFilter}
                                />
                            )}
                            {statusFilter === LiveEventStatusFilter.Active && (
                                <Chip
                                    size={'small'}
                                    label='Status: "In Progress"'
                                    onDelete={onResetStatusFilter}
                                />
                            )}
                            {statusFilter === LiveEventStatusFilter.Coming && (
                                <Chip
                                    size={'small'}
                                    label='Status: "Scheduled"'
                                    onDelete={onResetStatusFilter}
                                />
                            )}
                            {statusFilter === LiveEventStatusFilter.Completed && (
                                <Chip
                                    size={'small'}
                                    label='Status: "Completed"'
                                    onDelete={onResetStatusFilter}
                                />
                            )}
                            {developerOnly && (
                                <Chip
                                    size={'small'}
                                    icon={<DeveloperModeOutlined />}
                                    label='Player Type: "Developer only"'
                                    onDelete={onResetDeveloper}
                                />
                            )}
                        </div>
                    </Grid>
                }
            </Grid>
        );

        const onRowClick = (event: any, rowData: any) => this.toLink(`/liveEvent/${rowData.liveEvent.eventId}/details`);

        return (
            <Table
                styled={this.props.styled}
                tableRef={tableRef}
                title={title}
                columns = {columns}
                onRowClick={onRowClick}
                options = {{
                    selection: false,
                    paging: true,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 30, 50, 100],
                    emptyRowsWhenPaging: false,
                    sorting: false,
                    draggable: false,
                    search: true,
                    actionsColumnIndex: -1,
                }}
                data = {this.getLiveEvents}
                actions = {
                    userTitleRole > UserTitleRole.Viewer ?
                        [
                            rowData => ({
                                hidden: !isComing(rowData.liveEvent.startTimestamp, rowData.liveEvent.endTimestamp),
                                icon: ModeEditOutlineOutlined,
                                tooltip: 'Edit',
                                iconProps: {
                                    color: 'primary',
                                },
                                onClick: () => this.editLiveEvent(rowData.liveEvent)
                            }),
                            rowData => ({
                                icon: LibraryAddOutlined,
                                tooltip: 'Duplicate',
                                iconProps: {
                                    color: 'primary',
                                },
                                onClick: () => this.duplicateLiveEvent(rowData.liveEvent)
                            }),
                            rowData => ({
                                icon: DeleteOutlineOutlined,
                                tooltip: 'Delete',
                                iconProps: {
                                    color: 'primary',
                                },
                                onClick: () => this.deleteLiveEvent(rowData.liveEvent)
                            }),
                        ]
                        : undefined
                }
            />
        );
    };

    private getLiveEvents = async (query: any) => {
        const { typeFilter, statusFilter, developerOnly } = this.state;
        const { search, page, pageSize } = query;

        const list = await ActionRequest.get<Entities.ListLiveEventResponse>(`liveEvent/listByPage`, {
            name: search,
            typeFilter,
            statusFilter,
            developerOnly,
            page: page + 1,
            perPage: pageSize,
        });


        if (!list) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }

        const data = list.liveEvents.map(e => {
                const eventDetails = this.getDetails(e);
                const eventSchedule = this.getSchedule(e);
                return {
                    liveEvent: e,
                    details: eventDetails,
                    schedule: eventSchedule,
                };
            }
        );
        return {
            data,
            page: list.page - 1,
            totalCount: list.totalCount,
        };
    };

    private getDetails = (liveEvent: Entities.LiveEvent) => {
        const to = `/liveEvent/${liveEvent.eventId}/details`;
        let icon: React.JSX.Element = (<></>);
        switch (liveEvent.eventType) {
            case EventType.Regular:
                icon = <EventOutlined style={{width: 25, height: 25}}/>;
                break;
            case EventType.LeaderBoard:
                icon = <BarChartOutlined style={{width: 25, height: 25}}/>;
                break;
            default:
                break;
        }

        return (
            <div>
                {liveEvent.developerOnly && (
                    <Chip
                        color={'secondary'}
                        icon={<DeveloperModeOutlined />}
                        size='small'
                        label='Developer Only'
                        style={{ backgroundColor: '#21BA47', color: 'white' }}
                    />
                )}
                <div style={{ display: 'flex' }}>
                    {this.props.styled.smallUp &&
                        <div style={{
                            padding: '4px 0px',
                            display: 'grid',
                            justifyContent: 'flex-end',
                            gridAutoFlow: 'column',
                        }}>
                            {icon}
                        </div>
                    }
                    <div style={{display: 'inline-block', marginLeft: 10, verticalAlign: 'top'}}>
                        <Link to={to}>
                            {liveEvent.name !== '' ? truncateString(liveEvent.name) : liveEvent.eventId}
                        </Link>
                        <div>
                            <div style={{ fontWeight: 500 }}>Created</div>
                            {formatDate(liveEvent.createdTimestamp)}
                        </div>
                        <div>
                            <div style={{ fontWeight: 500 }}>Last Edited</div>
                            {formatDate(liveEvent.lastUpdatedTimestamp)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    private getSchedule = (liveEvent: Entities.LiveEvent) => {
        if (!liveEvent.timerType) {
            return (
                <div>
                    <div>
                        {this.getStatus(liveEvent)}
                    </div>
                    <div>
                        <div style={{ fontWeight: 500 }}>Start</div>
                        {formatDate(liveEvent.startTimestamp)}
                    </div>
                    <div>
                        <div style={{ fontWeight: 500 }}>End</div>
                        {formatDate(liveEvent.endTimestamp)}
                    </div>
                </div>
            );
        }

        switch (liveEvent.timerType) {
            case TimerType.Scheduled:
                return (
                    <div>
                        <div>
                            {this.getStatus(liveEvent)}
                        </div>
                        <div>
                            <div style={{ fontWeight: 500 }}>Start</div>
                            {formatDate(liveEvent.startTimestamp)}
                        </div>
                        <div>
                            <div style={{ fontWeight: 500 }}>End</div>
                            {formatDate(liveEvent.endTimestamp)}
                        </div>
                    </div>
                );
            case TimerType.Recurring:
                if (!isActive(liveEvent.startTimestamp, liveEvent.endTimestamp)) {
                    return (
                        <div>
                            <div style={{ color: 'red', fontWeight: 500 }}>Finished</div>
                            <div>
                                <div style={{ fontWeight: 500 }}>Started</div>
                                {formatDate(liveEvent.startTimestamp, 'LL')}
                            </div>
                            <div>
                                <div style={{ fontWeight: 500 }}>Ended</div>
                                {formatDate(liveEvent.endTimestamp, 'LL')}
                            </div>
                        </div>
                    );
                }

                const recurringEntity = new RecurringEntity(liveEvent);
                return (
                    <div>
                        <div>
                            {this.getStatus(liveEvent)}
                        </div>
                        <div>
                            <div style={{ fontWeight: 500 }}>Start</div>
                            {formatDate(liveEvent.startTimestamp, 'LL')}
                        </div>
                        <div>
                            <div style={{ fontWeight: 500 }}>End</div>
                            {formatDate(liveEvent.endTimestamp, 'LL')}
                        </div>
                        <div>
                            <div style={{ fontWeight: 500 }}>Recurring</div>
                            {recurringEntity.getRecurringText()}
                        </div>
                    </div>
                );
        }
    };

    private getStatus = (liveEvent: Entities.LiveEvent) => {
        if (!liveEvent.timerType || liveEvent.timerType != TimerType.Recurring) {
            if (isActive(liveEvent.startTimestamp, liveEvent.endTimestamp)) {
                return <div style={{color: 'green', fontWeight: 500}}>In Progress</div>;
            }
            if (isEnded(liveEvent.endTimestamp)) {
                return <div style={{color: 'orange', fontWeight: 500}}>Completed</div>;
            }
            return <div style={{color: 'grey', fontWeight: 500}}>Scheduled</div>;
        }

        if (isEnded(liveEvent.endTimestamp)) {
            return <div style={{color: 'orange', fontWeight: 500}}>Completed</div>;
        }

        if (isActiveTimerEntity(liveEvent)) {
            return <div style={{color: 'green', fontWeight: 500}}>In Progress</div>;
        }

        return <div style={{color: 'grey', fontWeight: 500}}>Scheduled</div>;
    };

    private editLiveEvent = (liveEvent: Entities.LiveEvent) => this.toLink(`/liveEvent/${liveEvent.eventId}/edit`);

    private duplicateLiveEvent = (liveEvent: Entities.LiveEvent) => this.toLink(`/liveEvent/${liveEvent.eventId}/duplicate`);

    private deleteLiveEvent = (liveEvent: Entities.LiveEvent) => this.setState({openDialog: 'DeleteLiveEvent', selectedLiveEvent: liveEvent});

    private renderDeleteLiveEventDialog = () => {
        const { openDialog, selectedLiveEvent } = this.state;
        if (!selectedLiveEvent) {
            return;
        }
        const title = `Delete live event '${selectedLiveEvent ? selectedLiveEvent.name : ''}'?`;

        return (
            <WarningDialog
                styled={this.props.styled}
                open={openDialog === 'DeleteLiveEvent'}
                title={title}
                TitleIcon = {CalendarTodayIcon}
                content="This will permanently delete the event and all associated configuration."
                onClose={this.onCloseDialog}
                onSubmit={this.onDeleteLiveEvent}
            />
        );
    };
    private onDeleteLiveEvent = async () => {
        const { selectedLiveEvent } = this.state;
        if (!selectedLiveEvent) {
            return;
        }
        await actions.liveEvent.remove(selectedLiveEvent.eventId);
    };

    private onCloseDialog = () => this.setState({ openDialog: null, selectedLiveEvent: null});
}

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate}/>);
};
export default connect(mapStateToProps)(AppContainer);
